import React, { useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Link as LinkIcon,
  PersonOff as PersonOffIcon,
  PersonAdd as PersonAddIcon,
  VpnKey as KeyIcon
} from '@mui/icons-material';

import LinkGenerator from './LinkGenerator';
import ToggleUserActiveModal from './ToggleUserActiveModal';
import { usePermissions } from '../contexts/permissions';

const UserMoreOptionsMenu = ({ entity, disabled, setEntities, activeFilter }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [showInvitationDialog, setShowInvitationDialog] = useState(false);
  const [showPasswordResetDialog, setShowPasswordResetDialog] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const { can } = usePermissions();

  const open = Boolean(anchorEl);
  const canGenerateInvitationLink = !entity.invitationAccepted;
  const canGeneratePasswordResetLink = !!entity.invitationAccepted;
  const canToggleActive = can('users.destroy');
  const isDisabling = activeFilter === true;

  if (!canGenerateInvitationLink && !canGeneratePasswordResetLink && !canToggleActive) {
    return null;
  }

  const handleClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleGenerateInvitationLink = () => {
    handleClose();
    setShowInvitationDialog(true);
  };

  const handleGeneratePasswordResetLink = () => {
    handleClose();
    setShowPasswordResetDialog(true);
  };

  const handleToggleUser = () => {
    handleClose();
    setShowConfirm(true);
  };

  return (
    <>
      <Tooltip title="More options">
        <IconButton
          size="small"
          onClick={handleClick}
          disabled={disabled}
          aria-label="more options"
          aria-controls={open ? 'user-more-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
        >
          <MoreVertIcon />
        </IconButton>
      </Tooltip>

      <Menu
        id="user-more-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'user-more-button',
        }}
      >
        {canGenerateInvitationLink && (
          <MenuItem onClick={handleGenerateInvitationLink}>
            <ListItemIcon>
              <LinkIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Generate Invitation Link" />
          </MenuItem>
        )}

        {canGeneratePasswordResetLink && (
          <MenuItem onClick={handleGeneratePasswordResetLink}>
            <ListItemIcon>
              <KeyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Generate Password Reset Link" />
          </MenuItem>
        )}

        {canToggleActive && (
          <MenuItem onClick={handleToggleUser}>
            <ListItemIcon>
              {isDisabling ? <PersonOffIcon fontSize="small" /> : <PersonAddIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText primary={isDisabling ? "Disable User" : "Enable User"} />
          </MenuItem>
        )}
      </Menu>

      {showInvitationDialog && (
        <LinkGenerator
          type="invitation"
          userId={entity.id}
          userEmail={entity.email}
          open={showInvitationDialog}
          onClose={() => setShowInvitationDialog(false)}
        />
      )}

      {showPasswordResetDialog && (
        <LinkGenerator
          type="passwordReset"
          userId={entity.id}
          userEmail={entity.email}
          open={showPasswordResetDialog}
          onClose={() => setShowPasswordResetDialog(false)}
        />
      )}

      {showConfirm && (
        <ToggleUserActiveModal
          open={showConfirm}
          onClose={() => setShowConfirm(false)}
          entity={entity}
          isDisabling={isDisabling}
          onSuccess={setEntities}
        />
      )}
    </>
  );
};

export default UserMoreOptionsMenu;
